import herdService from '../services/herds';
import storageService from '../utils/localStorage';
import type { SubState, SubInfo, ReduxAction } from '../types/common';
import type { AppDispatch, RootState } from '../store';

const herdReducer = (state: SubState | null = null, action: ReduxAction<unknown>): SubState | null => {
  switch (action.type) {
    case 'SET_ALL_SUBS_LIST':
      return { ...state, allHerds: action.payload as ReadonlyArray<unknown> } as SubState;
    case 'SET_TOP_SUBS_LIST':
      return { ...state, topHerds: action.payload as ReadonlyArray<unknown> } as SubState;
    case 'ADJUST_MEMBER_COUNT': {
      const { id, delta } = action.payload as { id: string; delta: number };
      const bump = (list: SubInfo[] = []) =>
        list.map((s) =>
          s.id !== id ? s : { ...s, memberCount: Math.max(0, (s.memberCount || 0) + delta) }
        );
      return {
        ...state,
        allHerds: bump(state?.allHerds),
        topHerds: bump(state?.topHerds),
      } as SubState;
    }
    case 'ADD_NEW_HERD':
      return {
        ...state,
        allHerds: [...(state?.allHerds || []), action.payload as unknown],
      } as SubState;
    default:
      return state;
  }
};

export const setHerdList = () => {
  return async (dispatch: AppDispatch) => {
    const herds = await herdService.getAllHerds();

    dispatch({
      type: 'SET_ALL_SUBS_LIST',
      payload: herds,
    });
  };
};

export const setTopHerdsList = () => {
  return async (dispatch: AppDispatch) => {
    const top10Subs = await herdService.getTopHerds();

    dispatch({
      type: 'SET_TOP_SUBS_LIST',
      payload: top10Subs,
    });
  };
};

export const addNewHerd = (herdObj: Record<string, unknown>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const createdSub = await herdService.createHerd(herdObj);

    dispatch({
      type: 'ADD_NEW_HERD',
      payload: {
        herdName: createdSub.herdName,
        id: createdSub.id,
      },
    });

    // The server auto-joins the creator; mirror that locally so the new
    // herd immediately shows up in joined-herd pickers.
    const currentUser = getState().user;
    if (currentUser) {
      const ids = [...(currentUser.joinedHerds as string[] || []), createdSub.id];
      dispatch({ type: 'SET_JOINED_HERDS', payload: ids });
      const stored = storageService.loadUser();
      if (stored) storageService.saveUser({ ...stored, joinedHerds: ids });
    }

    return createdSub as { id: string; herdName: string };
  };
};

export default herdReducer;